import { Formiz, useForm } from "@formiz/core";
import { isMinLength, isRequired } from "@formiz/validations";
import { applyRulebookAttachmentChange, NotificationType, usePersona, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import useRulebookAttachmentSelection from "@next-platform/client-common-core/rulebook/attach/useRulebookAttachmentSelection";
import { Button, ButtonSize, ButtonVariant, Checkbox, Input, Size, TextArea } from "@next-platform/client-common-spicy-ui";
import { CreateOrUpdatePersonaRequest } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AvatarCreate from "@/components/CharacterForm/AvatarCreateV2";
import UnsavedChangesGuard, { type UnsavedChangesGuardHandle } from "@/components/common/UnsavedChangesGuard";
import { FormField } from "@/components/customComponents";
import RulebookAttachmentField from "@/components/Rulebook/RulebookAttachmentField";
import Trans from "@/components/Trans/Trans";
import { PERSONA_FIELD_LIMITS, PERSONA_FIELD_MIN_LENGTH, ROUTES } from "@/constants";
import useToast from "@/hooks/useToast";
import { PLACEHOLDER_STYLING } from "@/pages/CreateCharacter/v2/constants";
import { FormTypes } from "@/types";
import { isPersonasLimitReached } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

import LimitReachedModal from "../LimitReachedModal";
import hasPersonaFormChanges from "./hasPersonaFormChanges";
import { PersonaFormProps } from "./types";

const PersonaForm = (props: PersonaFormProps) => {
  const { t } = useTranslation();
  const { initialValues: initialValuesProp, edit = false } = props;

  const normalizedInitialValues = useMemo(
    () => ({
      name: initialValuesProp?.name ?? "",
      highlights: initialValuesProp?.highlights ?? "",
      avatar_url: initialValuesProp?.avatar_url ?? "",
      is_default: initialValuesProp?.is_default ?? false,
    }),
    [initialValuesProp?.avatar_url, initialValuesProp?.highlights, initialValuesProp?.is_default, initialValuesProp?.name],
  );

  const { role } = useUserInfo();
  const { personaId } = useParams();
  const navigate = useNavigate();

  const { personas, onUserCreatePersonaV2, onUserUpdatePersonaV2, success, loading, resetError, errorMessage } = usePersona();
  const rulebookSelection = useRulebookAttachmentSelection({ ownerType: "persona", ownerId: personaId ?? "" });

  const personaForm = useForm();
  const { setFieldsValues, isValid } = personaForm;
  const hasUnsavedChanges = useMemo(
    () =>
      hasPersonaFormChanges({
        values: personaForm.values ?? {},
        initialValues: normalizedInitialValues,
        edit,
      }) || rulebookSelection.hasChanged,
    [edit, normalizedInitialValues, personaForm.values, rulebookSelection.hasChanged],
  );

  const isSubmitVisuallyDisabled = !isValid || (edit && !hasUnsavedChanges);

  const [stateIsSubmitAttempted, setStateIsSubmitAttempted] = useState(false);
  const [stateIsLimitReachedModal, setStateIsLimitReachedModal] = useState(false);
  // Navigation waits for the rulebook attach to settle so the request isn't cut mid-flight.
  const [stateIsAttaching, setStateIsAttaching] = useState(false);
  // Holds navigation after a submit that left the form open (failed attach on edit, or a failed save
  // while `success` is still true from an earlier save). Released only once a later save succeeds.
  const [stateIsNavigationHeld, setStateIsNavigationHeld] = useState(false);

  const isPartner = getIsPartner();
  const { displayToast } = useToast();
  const refUnsavedChangesGuard = useRef<UnsavedChangesGuardHandle>(null);

  const handleCancel = useCallback(() => {
    refUnsavedChangesGuard.current?.handleCancel();
  }, []);

  // The persona itself saved — only the attachment failed. On edit the form stays open for a retry;
  // on create the persona already exists, so navigation proceeds and the attach is redone from Edit.
  const handleAttachFailure = useCallback(() => {
    displayToast({ message: t("persona:form.rulebookAttachFailed"), type: NotificationType.error });
    if (edit) {
      setStateIsNavigationHeld(true);
      refUnsavedChangesGuard.current?.resetSaveNavigationBypass();
    }
  }, [displayToast, edit, t]);

  const handleSubmitButtonClick = useCallback(() => {
    if (edit && !hasUnsavedChanges) {
      displayToast({ message: t("error:noChangesDetected"), type: NotificationType.info });
      return;
    }
    setStateIsSubmitAttempted(true);
    personaForm.submit();
  }, [displayToast, edit, hasUnsavedChanges, personaForm, t]);

  const submitForm = useCallback(async () => {
    if (isPersonasLimitReached(personas, role, edit, isPartner)) {
      setStateIsLimitReachedModal(true);
      return;
    }

    refUnsavedChangesGuard.current?.markSaveNavigationAsSafe();

    const { avatar_url, name, highlights, is_default } = personaForm.values;
    const payload: CreateOrUpdatePersonaRequest = { avatar_url, name, highlights };

    // Flag the attach before the save resolves: `success` flips in the same batch and the navigation
    // effect must see an attach still in flight.
    const isRulebookAttachPending = rulebookSelection.hasChanged;
    if (isRulebookAttachPending) {
      setStateIsAttaching(true);
    }

    const result = edit && personaId ? await onUserUpdatePersonaV2(personaId, payload) : await onUserCreatePersonaV2({ ...payload, is_default });

    if (result?.error) {
      setStateIsAttaching(false);
      setStateIsNavigationHeld(true);
      refUnsavedChangesGuard.current?.resetSaveNavigationBypass();
      return;
    }

    setStateIsNavigationHeld(false);
    const savedPersonaId = edit && personaId ? personaId : result?.data?.id;
    if (savedPersonaId && isRulebookAttachPending) {
      try {
        await applyRulebookAttachmentChange({ ownerType: "persona", ownerId: savedPersonaId, ...rulebookSelection.change });
        rulebookSelection.markSaved();
      } catch {
        handleAttachFailure();
      } finally {
        setStateIsAttaching(false);
      }
    } else {
      setStateIsAttaching(false);
      rulebookSelection.markSaved();
    }
  }, [
    edit,
    handleAttachFailure,
    isPartner,
    onUserCreatePersonaV2,
    onUserUpdatePersonaV2,
    personaForm.values,
    personaId,
    personas,
    role,
    rulebookSelection,
    t,
  ]);

  const handleValidSubmit = useCallback(() => {
    void submitForm();
  }, [submitForm]);

  useEffect(() => {
    if (errorMessage) {
      displayToast({ message: errorMessage, type: NotificationType.error });
      resetError();
    }
  }, [displayToast, errorMessage, resetError]);

  useEffect(() => {
    if (success && !stateIsAttaching && !stateIsNavigationHeld) {
      if (edit && personaId) {
        displayToast({ message: t("personas:page.editPersona.toast.success"), type: NotificationType.success });
      } else {
        displayToast({ message: t("personas:page.createPersona.toast.success"), type: NotificationType.success });
      }
      navigate(ROUTES.personas, { state: "create/edit-persona" });
    }
  }, [displayToast, edit, navigate, personaId, stateIsNavigationHeld, stateIsAttaching, success, t]);

  return (
    <>
      <UnsavedChangesGuard ref={refUnsavedChangesGuard} hasUnsavedChanges={hasUnsavedChanges} isSubmitting={loading?.isLoading ?? false} />

      <Formiz connect={personaForm} initialValues={normalizedInitialValues} onValidSubmit={handleValidSubmit}>
        <form onSubmit={personaForm.submit} className="flex flex-col gap-xl ">
          <FormField
            as="input"
            name="avatar_url"
            showError={stateIsSubmitAttempted}
            validations={[{ rule: isRequired(), message: t("personas:form.validation.required.avatar") }]}
            dataTestId="personaImageUploadFormField"
          >
            <AvatarCreate
              characterAvatar={personaForm.values.avatar_url}
              setCharacterAvatar={(value) => setFieldsValues({ avatar_url: value })}
              form={personaForm}
              formType={FormTypes.PERSONA}
              persona={true}
              required={true}
            />
          </FormField>
          <FormField
            as="input"
            name="name"
            showError={stateIsSubmitAttempted}
            validations={[
              { rule: (value: string) => isRequired()(value?.trim()), message: t("personas:form.validation.required") },
              {
                rule: (value: string) => isMinLength(PERSONA_FIELD_MIN_LENGTH)(value?.trim()),
                message: t("personas:form.validation.minLength"),
              },
            ]}
          >
            <Input
              required
              label={t("personas:form.name.label")}
              helperText={t("personas:form.name.helperText")}
              placeholder={t("personas:form.name.placeholder")}
              maxLength={PERSONA_FIELD_LIMITS.NAME}
              value={personaForm.values.name}
              classNames={{ input: PLACEHOLDER_STYLING }}
            />
          </FormField>
          <FormField
            as="input"
            name="highlights"
            showError={stateIsSubmitAttempted}
            validations={[
              { rule: (value: string) => isRequired()(value?.trim()), message: t("personas:form.validation.required") },
              {
                rule: (value: string) => isMinLength(PERSONA_FIELD_MIN_LENGTH)(value?.trim()),
                message: t("personas:form.validation.minLength"),
              },
            ]}
          >
            <TextArea
              label={t("personas:form.highlight.label")}
              helperText={t("personas:form.highlight.helperText")}
              placeholder={t("personas:form.highlight.placeholder")}
              maxLength={PERSONA_FIELD_LIMITS.HIGHLIGHT}
              required
              rows={4}
              className={PLACEHOLDER_STYLING}
            />
          </FormField>
          <RulebookAttachmentField selection={rulebookSelection} disabled={loading?.isLoading} dataTestId="PersonaForm-RulebookSection" />
          {!edit ? (
            <FormField name="is_default" as="checkbox">
              <Checkbox label={t("personas:form.setAsDefaultPersona")} size={Size.MEDIUM} />
            </FormField>
          ) : null}
          <div className="flex justify-end gap-md">
            <Button
              name="cancel"
              disabled={loading?.isLoading}
              variant={ButtonVariant.OUTLINED}
              size={ButtonSize.LARGE}
              type="button"
              onClick={handleCancel}
            >
              <Trans i18nKey="common:cancel" />
            </Button>
            <Button
              name="submit"
              type="button"
              size={ButtonSize.LARGE}
              onClick={handleSubmitButtonClick}
              aria-disabled={isSubmitVisuallyDisabled}
              className={isSubmitVisuallyDisabled ? "opacity-50 cursor-not-allowed" : ""}
              loading={loading?.isLoading}
            >
              {edit ? <Trans i18nKey="personas:form.button.update" /> : <Trans i18nKey="personas:form.button.create" />}
            </Button>
          </div>
        </form>
      </Formiz>
      <LimitReachedModal isOpen={stateIsLimitReachedModal} onClose={() => setStateIsLimitReachedModal(false)} />
    </>
  );
};

export default PersonaForm;
